<template>
<section data-component="PromotionSearch.vue" class="promotion-search-list mt-14 mb-6 ml-5 search" :class="{'pb-1': suggestions.length, 'pb-4': !suggestions.length, 'mb-20': isMobile}">
	<div class="flex items-center justify-between mb-6" :class="{'ml-5': ScreenSize < 1}">
		<div class="heading-normal" :class="{'flex': ScreenSize >= 1}">
			Wybierz listy i włącz Promowanie

			<TooltipElement class="promotion-tooltip" :class="{'inline-block': ScreenSize < 1}">
				<template #tooltipContent>
					<div class="font-light">
						Czym są listy firm w Oferteo?
						Listy firm, to specjalne strony, które są dedykowane danej kategorii oraz lokalizacji (np. Malowanie ścian Wrocław).
						Pokazywane są na nich firmy, które wykonują dane usługi. Na te strony trafiają Zleceniodawcy i to tu najczęściej składają zapytania.
						Mogą też obejrzeć profile firm widocznych na liście firm.
					</div>
				</template>
				<template #tooltipTrigger>
					<div class="ml-1">
						<BaseIcon name="info-information-circle" :size="1.1" class="mt-1"/>
					</div>
				</template>
			</TooltipElement>
		</div>
	</div>

	<div class="flex search-form">
		<!--category form -->
		<div class="col-half mr-2 form-input mb-0 search-form__category">
			<div class="autocomplete input input-category-wrapper">
				<button v-show="categoryInput.length > 0" class="clear-input-button p-5" @click="clearCategoryInput">
					<BaseIcon name="close" size="0.7" class="mt-1"/>
				</button>

				<div class="input-icon p-5">
					<BaseIcon name="search" class="mt-1 text-neutral-500"/>
				</div>

				<input
					id="category" v-model="categoryInput" type="text" autocomplete="off"
					placeholder="Szukaj po nazwie usługi" @input="getCategoriesBasedOnInput"
					@click="showSearchCategoriesSuggestions = true"
				>
				<div v-show="showSearchCategoriesSuggestions" ref="list" class="suggestions-list suggestion-list__promotion">
					<a
						v-for="(category, index) in categories" :key="`category-${index}`" class="cursor-pointer"
						@click="setCategory(category)"
					>{{ category.name }}</a>
				</div>
			</div>
		</div>

		<!--location form-->
		<div class="col-half mr-2 form-input mb-0">
			<div class="autocomplete input input-location-wrapper">
				<button v-show="locationInput.length > 0" class="clear-input-button clear-location p-5" @click="clearLocationInput">
					<BaseIcon name="close" size="0.7" class="mt-1"/>
				</button>

				<div class="input-icon p-5">
					<BaseIcon name="pin" class="mt-1 text-neutral-500"/>
				</div>

				<input
					id="localization" ref="locationInput" v-model="locationInput" autocomplete="off" :class="!selectedCategoryId || categoryInput.length < 1 ? 'disabled-input' : 'active-input'"
					type="text" placeholder="Określ lokalizację (opcjonalne)" :disabled="!selectedCategoryId || categoryInput.length < 1"
					@click="showSearchLocationsSuggestions = true"
					@input="getLocationsBasedOnInput"
				>

				<div v-show="showSearchLocationsSuggestions" ref="list" class="suggestions-list suggestion-list__promotion">
					<a v-for="(location, index) in locations" :key="`suggestion-${index}`" @click="setLocation(location)">
						{{ location.city }}
					</a>
				</div>

				<BaseIcon name="map-location-search" size="1.5" class="map-icon" :class="{'disabled-input' : !selectedCategoryId || categoryInput.length < 1}" @click="toggleMapModal()"/>
			</div>
		</div>
	</div>

	<div class="suggestions-checkbox-filter-wrapper">
		<div class="input-control mx-3">
			<input
				id="suggestions-checkbox-filter"
				v-model="suggestionsCheckboxFilter" type="checkbox"
			>
			<BaseIcon v-if="suggestionsCheckboxFilter" name="promotion-checkbox" class="promotion-checkbox" size="1.5"/>
			<div v-else class="promotion-checkbox"/>
		</div>
		<label for="suggestions-checkbox-filter">Pokaż tylko te usługi, które wykonuję</label>
	</div>

	<PromotionTable
		:module="'search'"
		:promotions="promotionsReducedByEnabled"
		:isPromotionsLimitReached="isPromotionsLimitReached"
		class="max-h-72 mb-12"
	/>
</section>
</template>
<script lang="ts">
import { defineAsyncComponent, PropType } from 'vue';
import { mapState, mapActions, mapWritableState } from 'pinia';
import { useAppStore } from '../../../stores/app';
import { usePromotionStore } from '../../../stores/promotion';
import SuggestedPromotion from '../../../types/interfaces/promotion/SuggestedPromotion';
import EnabledPromotion from '../../../types/interfaces/promotion/EnabledPromotion';
import Promotion from '../../../types/interfaces/promotion/Promotion';
import serverFetch from '../../../globals/ServerFetch';

export default {
	name: 'PromotionSearch',
	components: {
		PromotionTable: defineAsyncComponent(() => import('./common/PromotionTable.vue')),
		TooltipElement: defineAsyncComponent(() => import('../../../globals/components/global/TooltipElement.vue')),
	},
	props: {
		apiNamespace: {
			type: String,
			required: true,
		},
		isPromotionsLimitReached: {
			type: Boolean,
			required: true,
		},
		enabledPromotions: {
			type: Array as PropType<EnabledPromotion[]>,
			required: true,
		},
	},
	emits: ['add-to-cart', 'get-all-locations-per-category', 'toggle-map-modal'],
	data() {
		return {
			categories: [] as {id: string, name: string}[],
			locations: [] as {id: string, city: string, active: boolean}[],
			showSearchCategoriesSuggestions: false,
			showSearchLocationsSuggestions: false,
			debounceCategorySearch: 0,
			debounceLocalizationSearch: 0,
			promotionsCurrentSort: 'name',
			promotionsCurrentSortDir: 'asc',
		};
	},
	computed: {
		...mapState(useAppStore, [
			'isAdminModule',
		]),
		...mapState(usePromotionStore, [
			'customerId',
			'cartPromotions',
			'suggestions',
		]),
		...mapWritableState(usePromotionStore, [
			'suggestionsCheckboxFilter',
			'categoryInput',
			'locationInput',
			'selectedCategoryId',
			'selectedLocationId',
		]),

		isMobile() {
			return (this.$root as any).$data.WindowWidth <= 768;
		},
		promotionsReducedByEnabled(): SuggestedPromotion[] {
			return this.suggestions.filter((suggestionItem) => !this.enabledPromotions.find((enabledPromotionItem) => enabledPromotionItem.listUrl === suggestionItem.listUrl));
		},
		sortedPromotions() {
			// eslint-disable-next-line vue/no-mutating-props, vue/no-side-effects-in-computed-properties
			return this.promotionsReducedByEnabled.sort((promotionA, promotionB) => {
				const modifier = this.promotionsCurrentSortDir === 'desc' ? -1 : 1;

				const promotionAValue = promotionA[this.promotionsCurrentSort as keyof Promotion] as number | string;
				const promotionBValue = promotionB[this.promotionsCurrentSort as keyof Promotion] as number | string;

				if (typeof promotionAValue === 'string' && typeof promotionBValue === 'string') {
					return promotionAValue.localeCompare(promotionBValue) * modifier;
				}

				if (!promotionBValue || !promotionAValue) {
					return 0;
				}

				return (Number(promotionBValue) - Number(promotionAValue)) * modifier;
			});
		},
	},
	watch: {
		suggestionsCheckboxFilter() {
			if (this.selectedCategoryId) {
				this.getSuggestions();
			}
		},
	},
	mounted() {
		this.getPromotionRecommendations();
		this.getSuggestedCategories();
		this.getSuggestedLocations();
	},
	methods: {
		...mapActions(usePromotionStore, [
			'addToCart',
			'getPromotionRecommendations',
			'getSuggestions',
			'clearCategoryInput',
		]),

		async getSuggestedCategories() {
			try {
				const { result } = await serverFetch(`/${this.apiNamespace}/get-suggested-customer-promotion-categories.ofer?idCustomer=${this.customerId}`);

				this.processCategories(result);
			} catch (error) {
				console.error(error);
			}
		},
		getCategoriesBasedOnInput({ target }: Event) {
			if (this.debounceCategorySearch) {
				clearTimeout(this.debounceCategorySearch);
			}

			const { value: queryParam } = target as HTMLInputElement;

			this.debounceCategorySearch = window.setTimeout(async () => {
				const { result } = await serverFetch(`/${this.apiNamespace}/get-recommended-promotion-categories.ofer?inputCategory=${queryParam}&idCustomer=${this.customerId}`);

				this.processCategories(result);
			}, 600);
		},
		async getSuggestedLocations() {
			try {
				const { result } = await serverFetch(`/${this.apiNamespace}/get-suggested-customer-promotion-locations.ofer?idCustomer=${this.customerId}`);

				this.processLocations(result);
			} catch (error) {
				console.error(error);
			}
		},
		getLocationsBasedOnInput({ target }: Event) {
			if (this.debounceLocalizationSearch) {
				clearTimeout(this.debounceLocalizationSearch);
			}

			const { value: queryParam } = target as HTMLInputElement;

			this.debounceLocalizationSearch = window.setTimeout(async () => {
				const url = this.isAdminModule ? `/${this.apiNamespace}/get-recommended-promotion-locations.ofer?inputLocation=${queryParam}&idCustomer=${this.customerId}` : `/${this.apiNamespace}/get-recommended-promotion-locations.ofer?inputLocation=${queryParam}`;

				try {
					const { result } = await serverFetch(url);

					this.processLocations(result);
				} catch (error) {
					console.error(error);
				}
			}, 600);
		},
		processCategories(categories: Record<string, string>[]) {
			this.categories = [];

			categories.forEach((category) => {
				this.categories.push({
					id: Object.entries(category)[0][0],
					name: Object.entries(category)[0][1],
				});
			});
		},
		processLocations(locations : Record<string, string>[]) {
			this.locations = [];

			locations.forEach((location) => {
				this.locations.push({
					id: Object.entries(location)[0][0],
					city: Object.entries(location)[0][1],
					active: false,
				});
			});
		},
		setCategory({ name, id }: { name: string, id: string }) {
			this.categoryInput = name;
			this.selectedCategoryId = id;

			this.getSuggestions();
			this.getAllLocationsPerCategory(this.selectedCategoryId);
		},
		setLocation({ city, id }: { city: string, id: string }) {
			this.locationInput = city;
			this.selectedLocationId = id;

			this.getSuggestions();
		},
		shouldLabelBeDisplayed(item: SuggestedPromotion, type: string) {
			const counter = Number(item.isNew) + Number(item.isLastPlace) + Number(item.isPremium) + Number(item.isGainingPopularity);

			if (counter > 2 && (type === 'isPremium' || type === 'isGainingPopularity')) {
				return false;
			}

			return true;
		},
		getAllLocationsPerCategory(categoryId: string) {
			this.$emit('get-all-locations-per-category', categoryId);
		},
		toggleMapModal() {
			this.$emit('toggle-map-modal', true);
		},
	},
};
</script>
<style lang="scss" scoped>
.form-input {
	margin-bottom: 0;
}

.autocomplete.input.input-location-wrapper {
	border: 0;
}
.autocomplete.input {
	border-radius: 8px;
	max-height: 48px !important;
}
.input-location-wrapper input[type="text"], .input-category-wrapper input[type="text"] {
	padding-left: 50px;
	font-size: 16px;
	font-weight: 400;
	max-height: 48px;
	min-height: auto;
	border-radius: 8px;
}

.input-category-wrapper {
	max-height: 48px;
	min-height: auto;
}

@media (max-width: 768px) {
	input {
		font-size: 16px;
	}
}

.search-results__item--disabled {
	filter: grayscale(1);
	pointer-events: none;
	opacity: 0.7;
}

.clear-input-button {
	position: absolute;
	right: -5px;
	top: -6px;
}

.input-icon {
	position: absolute;
	left: -5px;
	top: -10px;
}

.clear-location {
	right: 30px;
	top: -6px;
}

#localization.disabled-input:hover, .map-icon.disabled-input:hover {
	cursor: no-drop !important;
}

#localization {
	&.disabled-input {
		background: #f2f2f2 !important;
	}
	&.active-input {
		transition: none;
		border: 1px solid #00000026 !important;
	}
}

.search {
	@media only screen and (max-width: 640px) {
		margin-left: 0;
	}
}

.search-form {
	max-height: 48px;

	@media only screen and (max-width: 640px) {
			display: inline-block;
			width: 96%;
			padding-left: 12px;
			padding-right: 6px;
	}

	&__category {
		@media only screen and (max-width: 640px) {
			margin-bottom: 16px;
		}
	}
}

.map-icon {
	position: absolute;
    right: 12px;
	top: 12px;
	cursor: pointer;

	:deep(path) {
		fill: gray;
	}
}

.suggestions-checkbox-filter-wrapper {
	display: flex;
	justify-content: flex-end;
	margin-top: 12px;
	border-radius: 4px;

	@keyframes fade-in {
		from { opacity: 0; }
		to { opacity: 1; }
	}
	.promotion-checkbox {
		animation: fade-in .15s;
	}

	@media only screen and (max-width: 640px) {
		justify-content: flex-start;
		margin-top: 24px;
		margin-left: 12px;
	}
}

.custom-tooltip {
	cursor: pointer;
	position: relative;
}

.custom-tooltip::before {
	content: attr(data-tooltip);
	position: absolute;
	padding: 1px 13px;
	display: none;
	color: #555;
	font-weight: normal;
	background: #ddd;
	top: 20px;
	left: -13px;
	border-radius: 4px;
	z-index: 10;
}

.custom-tooltip:hover::before {
	display: inline-block;
}

.search-results {
	@media only screen and (max-width: 640px) {
		padding-left: 0;
	}

	&__wrapper {
		@media only screen and (max-width: 640px) {
			border-left: 0;
			border-right: 0;
			border-radius: 0;
		}
	}

	&__heading {
		padding-right: 90px;
		border-radius: 8px;
		background: #f2f2f2;

		@media only screen and (max-width: 640px) {
			display: none;
		}
	}

	.sort-buttons span {
		&:nth-child(1) {
			display: block;
			width: 8px;
			height: 6px;
			clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
			background: #ccc;
			position: relative;
		}

		&:nth-child(2) {
			display: block;
			margin-top: 3px;
			width: 8px;
			height: 6px;
			clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
			background: #ccc;
			transform: rotate(-180deg);
		}
	}
}

.promotion-tooltip :deep(.tooltip__text-content) {
	width: 20rem;
	background-color: #4381DC;
	font-size: 14px;
	font-weight: 400;
	color: #fff;
	padding: 12px;

	&::before {
		right: 90%;
		border-top: 9px solid #4381DC;
	}

	@media (min-width: 376px) and (max-width: 460px){
		left: -300px;

		&::before {
			right: 10px;
		}
	}

	@media (max-width: 375px) {
		left: -10px;

		&::before {
			right: 90%;
		}
	}
}
</style>
